import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import uniqid from "uniqid";
import { useAppDispatch, useAppSelector } from "../../Store";
import {
  allMessagesStore,
  removeMessage,
  setAllHistoryMessages,
  setAllMessages,
  setIsNextNodeAvailable,
  setIsSessionIdFromLocalStorage,
  setIsSmartAIEnabled,
  setLastNodeResponse,
  setOriginalResponses,
  setSessionId,
  setTypingStatus,
  updateMessage,
} from "../../Store/Slices/socket/AllMessagesSlice";
import { useBotConfigStore } from "../../Store/Slices/ChatbotSlices/BotConfigSlice";
import {
  generateConnectSocketToAgent,
  generateOutsideHoursMessage,
  isWithinBusinessHour,
  sleep,
} from "../utils/utils";
import { PreviewType, MESSAGE_TYPES } from "../utils/Enum";
import { botInfoStore } from "../../Store/Slices/socket/BotInfoSlice";
import { HostWindowCommService } from "../../Services/IframeHostService/HostWindowCommService";
import { OEvents } from "../../Services/IframeHostService/models";
import { ConversationService } from "../../Services/Apis";
import { CH_BOT_ENDPOINTS } from "../../Services/Apis/Endpoints";
import { conversationAxios } from "../../Utils/Axios";
import { socketStatusStore } from "../../Store/Slices/socket/SocketSlice";

let audio: any = new Audio(require("../../assets/tong.mp3"));
const SLEEP_TIME = 500;

const useMessagesData = () => {
  const commService = useMemo(() => {
    return HostWindowCommService.getInstance();
  }, []);

  //redux
  const dispatch = useAppDispatch();
  const { botConfig } = useAppSelector(useBotConfigStore);

  const { botId, visitorId, widgetPreviewType, getaHost, chatbotParams } =
    useAppSelector(botInfoStore);
  const {
    executeConnectSocketEventWithReConnectUser,
    socket,
    executeConnectSocketEventWithConnectUser,
  } = useAppSelector(socketStatusStore);
  const { sessionId, botLanguage, liveChatConnected } =
    useAppSelector(allMessagesStore);
  const [loading, setLoading] = useState(false);

  const directLiveChatEnabled = useMemo(
    () =>
      botConfig?.chatbot?.hasOwnProperty("bot_enabled")
        ? !botConfig.chatbot.bot_enabled
        : false,
    [botConfig]
  );

  const botName = useMemo(() => botConfig.chatbot?.name ?? "Bot", [botConfig]);
  const botHeaders = useMemo(
    () => ({
      appid: JSON.stringify({
        botid: botId,
      }),
      "geta-host": getaHost,
      visitor_id: visitorId || null,
      preview:
        widgetPreviewType === PreviewType.getaWidgetPreview ? true : false,
      connection_type: "user",
    }),
    [botId, visitorId, widgetPreviewType, getaHost]
  );

  // Create a ref for sessionId
  const sessionIdRef = useRef(sessionId);
  // Update the ref whenever sessionId changes to avoid closures scopes below.
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  // Create a ref for botLanguage
  const botLanguageRef = useRef(botLanguage);
  // Update the ref whenever botLanguage changes to avoid closures scopes below.
  useEffect(() => {
    botLanguageRef.current = botLanguage;
  }, [botLanguage]);

  const requestParams = useMemo(() => {
    const chatbotUrlParams = chatbotParams ?? {};
    const params = {
      session_id: sessionId, // Use direct value, not ref
      language: botLanguage, // Use direct value, not ref
      bot_enabled: botConfig?.chatbot?.hasOwnProperty("bot_enabled")
        ? botConfig.chatbot.bot_enabled
        : true,
      ...chatbotUrlParams,
    };
    console.log("[requestParams] Created:", params);
    return params;
  }, [chatbotParams, sessionId, botLanguage, botConfig?.chatbot]);

  // --------------------------------FUNCTIONS----------------
  const handleLiveUserConnect = async (id: string) => {
    if (!id) {
      return console.warn("session id not found");
    }

    if (liveChatConnected) {
      return console.warn("live chat already connected");
    }

    //detect if chat is within business hours
    const checkTimings = isWithinBusinessHour(botConfig.config.active_timings);

    const isChatWithInTime = checkTimings?.isChatWithInTime ?? true;
    const timelogs = checkTimings?.timelogs ?? {};

    const data = await generateConnectSocketToAgent(
      "Live chat",
      id,
      socket,
      timelogs
    );

    if (!isChatWithInTime) {
      handleOutsideBusinessTimeClick(id);
      return;
    }

    executeConnectSocketEventWithConnectUser(data);
  };

  const handleOutsideBusinessTimeClick = async (id: string) => {
    let message =
      botConfig.config?.active_timings?.outside_business_hours_message ??
      "Sorry, we are not available at this time. Please try again later.";
    const data = await generateOutsideHoursMessage(message, id, null);

    dispatch(setAllMessages([data]));

    dispatch(
      setLastNodeResponse({ input: false, input_type: null, session_id: id })
    );
  };

  const storeSessionId = (id: string) => {
    if (id === sessionIdRef.current) return;

    dispatch(setSessionId(id));
    dispatch(setIsSessionIdFromLocalStorage(false));

    //* do not store session id in local storage if widget preview type is getaWidgetPreview
    let tempId =
      widgetPreviewType !== PreviewType.getaWidgetPreview ? id : null;

    commService.postMessage({
      event_type: OEvents.CHATBOT_SESSION_ID,
      data: tempId,
    });
  };

  const handleNewMessages = async (data: any) => {
    if (!data) return;
    if (!Array.isArray(data)) return;
    if (data.length === 0) {
      dispatch(setTypingStatus(``));
      return;
    }

    // dispatch(setTypingStatus(`${botName} is typing...`));
    for (let element of data) {
      if (element?.subType === "delay") {
        if (element?.response && Array.isArray(element?.response) && element.response.length > 0) {
          const message = element?.response?.[0]?.value || "";
          if (message) {
            dispatch(setAllMessages(element?.response));
            dispatch(setIsSmartAIEnabled(element?.quickActions?.[0]));
            const delayDuration = Number(element?.delay) * 1000
            await sleep(delayDuration);
            continue;
          }
        }
      }
      await sleep(SLEEP_TIME);
      if (element?.["subType"] === "button") {
        let temp = {
          value: element?.["response"],
          type: "button",
          time: element?.["createdAt"],
        };
        dispatch(setAllMessages(temp));
      } else {
        dispatch(setAllMessages(element?.response));
        dispatch(setIsSmartAIEnabled(element?.quickActions?.[0]));
      }
      dispatch(setLastNodeResponse(element));
      dispatch(setOriginalResponses(element));
    }

    //set typing status
    dispatch(setTypingStatus(``));
    //sound
    playSoundLastBotMsg();

    //store session id
    const newSessionId = data?.[data?.length - 1]?.session_id ?? "";
    storeSessionId(newSessionId);
  };

  const handleHistoryMessages = async (history: any) => {
    console.log("[handleHistoryMessages] Processing history:", history);
    if (
      !history ||
      history?.chat_data?.length === 0 ||
      !Array.isArray(history?.chat_data)
    ) {
      console.warn("[handleHistoryMessages] No chat data found. Calling getNewMessages with existing session_id:", sessionIdRef.current);
      // Don't clear session_id here to avoid triggering MainUI useEffect reset
      // Just call getNewMessages with current requestParams (which has the session_id)
      // The API will handle creating a new conversation or continuing with this session
      if (directLiveChatEnabled) {
        getNewMessagesForDirectLivechat({
          ...requestParams,
          bot_enabled: false,
        });
      } else {
        getNewMessages({ ...requestParams });
      }
      return;
    }

    if (history?.user?.botid !== botId) {
      console.warn("[handleHistoryMessages] Bot ID mismatch. Starting new session.");
      // Clear session since bot_id doesn't match
      dispatch(setSessionId(null));
      dispatch(setIsSessionIdFromLocalStorage(false));
      getNewMessages({ ...requestParams, session_id: null });
      return;
    }

    let payload: any = [];
    let lastSmartAIQuickAction: any = null;

    history?.chat_data?.forEach((item: any, index: any) => {
      if (item?.["user_query_object"]) {
        let temp = {
          ...item?.["user_query_object"],
          time: item?.["createdAt"],
        };
        payload.push(temp);
      }

      if (item?.["bot_response"]?.["response"]?.length > 0) {
        //single card
        if (item?.["bot_response"]?.["subType"] === "button") {
          let temp = {
            value: item?.["bot_response"]?.["response"],
            type: "button",
            time: item?.["createdAt"],
          };
          payload.push(temp);
        } else {
          item?.["bot_response"]?.["response"]?.forEach((curRes: any) => {
            let temp = {
              ...curRes,
              time: item?.["createdAt"],
            };
            payload.push(temp);
          });
        }
      }

      if (item?.["bot_response"]?.["quickActions"]?.length > 0) {
        lastSmartAIQuickAction = item?.["bot_response"]?.["quickActions"]?.[0];
      }

      if (index + 1 === history?.chat_data?.length) {
        dispatch(
          setLastNodeResponse(
            item?.["user_query_object"] ?? item?.["bot_response"]
          )
        );
      }

      dispatch(setOriginalResponses(item?.["bot_response"]));
    });

    dispatch(setAllHistoryMessages(payload));
    dispatch(setIsSessionIdFromLocalStorage(true));
    dispatch(setIsSmartAIEnabled(lastSmartAIQuickAction));

    if (history?.user?.agent?.length > 0) {
      console.error("agent found");
      executeConnectSocketEventWithReConnectUser();
    }

    if (
      history?.user?.conversation_type === "livechat" &&
      !history?.user?.agent
    ) {
      dispatch(setAllHistoryMessages([]));
      dispatch(setSessionId(null));
      dispatch(setIsSessionIdFromLocalStorage(false));
      if (directLiveChatEnabled) {
        getNewMessagesForDirectLivechat({
          ...requestParams,
          session_id: null,
          bot_enabled: false,
        });
      } else {
        getNewMessages({ ...requestParams, session_id: null });
      }
    }
    await sleep(500);
    setLoading(false);
  };

  // --------------------------------API CALLS------------------

  //================= GET BOT MESSAGES =================
  const getNewMessagesForDirectLivechat = (request: any) => {
    const requestData = {
      ...request,
      session_id: request.session_id || sessionIdRef.current || null,
    };
    return new Promise((resolve, reject) => {
      ConversationService.getMessagesDirectLivechat(requestData, botHeaders)
        .then(async (response) => {
          handleNewMessages(response?.data);

          const res = response?.data;
          const newSessionId = res?.[res?.length - 1]?.session_id ?? "";
          setTimeout(() => {
            handleLiveUserConnect(newSessionId);
          }, 1000);
          
          resolve(response);
        })
        .catch((error) => {
          console.error("getNewMessagesForDirectLivechat api:", error);
          reject(error);
        })
        .finally(() => {
          setLoading(false);
        });
    });
  };

  // GET BOT MESSAGES FOR SMART AI STREAMING
  const getNewMessagesForSmartAI = async (data: any) => {
    try {
      dispatch(setTypingStatus(`${botName} is typing...`));

      // Ensure session_id is always included in the request
      const requestData = {
        ...data,
        session_id: data.session_id || sessionIdRef.current,
      };

      // Insert a placeholder bot message to stream into
      const placeholderMessageId = uniqid();
      const placeholder = {
        type: MESSAGE_TYPES.TEXT,
        isUser: false,
        value: "",
        text: "",
        time: new Date().toISOString(),
        session_id: requestData.session_id,
        conversation_id: data?.conversation_id,
        message_id: placeholderMessageId,
      };
      dispatch(setAllMessages([placeholder]));

      // Build streaming request
      const baseUrl = conversationAxios.defaults.baseURL || "";
      const streamingEndpoint = CH_BOT_ENDPOINTS.QUERY_DIRECT_SMARTAI.replace(
        /^\/+/,
        ""
      );
      const url = `${baseUrl.replace(/\/+$/, "")}/${streamingEndpoint}`;
      const headers: Record<string, string> = {
        Accept: "text/event-stream",
        "Content-Type": "application/json;charset=UTF-8",
      };
      // Merge bot headers (values may be boolean/number -> stringify)
      Object.entries(botHeaders as Record<string, any>).forEach(([k, v]) => {
        headers[k] = typeof v === "string" ? v : JSON.stringify(v);
      });

      const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(requestData),
      });

      if (!response.ok || !response.body) {
        throw new Error(`Streaming request failed: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedText = "";
      let sawDone = false;

      // Immediately render updates as chunks arrive
      const renderUpdate = (text: string) => {
        dispatch(
          updateMessage({
            message_id: placeholderMessageId,
            value: text,
          })
        );
      };

      // Extract text content from a chunk (handles SSE, JSON, or plain text)
      const extractContent = (chunk: string): { text: string; done: boolean; exactMatch: boolean } => {
        let extractedText = "";
        let isDone = false;
        let exactMatch = false;

        // Split into lines but also handle content without newlines
        const lines = chunk.split(/\r?\n/);
        
        for (const rawLine of lines) {
          const line = rawLine.trim();
          if (!line) continue;

          // Check for done markers
          if (line === "[DONE]" || line.toUpperCase() === "DONE" || line === "data: [DONE]") {
            isDone = true;
            continue;
          }

          // Handle SSE format: data: {...} or data: text
          let payload = line;
          if (line.startsWith("data:")) {
            payload = line.slice(5).trim();
            if (payload === "[DONE]" || payload.toUpperCase() === "DONE") {
              isDone = true;
              continue;
            }
          }

          // Try to parse as JSON
          try {
            const evt = JSON.parse(payload);
            
            // Check for done flags in JSON
            if (evt?.done === true || evt?.type === "done" || evt?.event === "done") {
              isDone = true;
              continue;
            }
            
            // Check for exact_match type - this means we should stop streaming and call regular query API
            if (evt?.type === "exact_match") {
              exactMatch = true;
              console.log("[Stream] Exact match detected, switching to non-streaming API");
              return { text: extractedText, done: isDone, exactMatch: true };
            }
            
            // Skip metadata
            if (evt?.type === "metadata") continue;

            // Extract delta or response content
            if (evt?.delta) {
              extractedText += evt.delta;
            } else if (evt?.response) {
              extractedText += evt.response;
            } else if (evt?.content) {
              extractedText += evt.content;
            } else if (evt?.text) {
              extractedText += evt.text;
            } else if (evt?.message) {
              extractedText += evt.message;
            }
          } catch {
            // Not JSON - treat as plain text delta
            if (payload.length > 0 && !payload.startsWith("{")) {
              extractedText += payload;
            }
          }
        }

        return { text: extractedText, done: isDone, exactMatch: false };
      };

      // Main streaming loop - process chunks as they arrive
      while (true) {
        const { value, done } = await reader.read();
        
        if (done) {
          console.log("[Stream] Reader done");
          break;
        }

        // Decode the chunk immediately
        const chunk = decoder.decode(value, { stream: true });
        console.log("[Stream] Received chunk:", chunk.length, "bytes");

        // Extract content from this chunk
        const { text: newText, done: chunkDone, exactMatch } = extractContent(chunk);

        // If exact_match detected, abort streaming and call regular API
        if (exactMatch) {
          console.log("[Stream] Exact match detected - aborting stream and calling /query API");
          
          // Cancel the reader
          await reader.cancel();
          
          // Remove the placeholder message
          dispatch(removeMessage({ message_id: placeholderMessageId }));
          
          // Call the regular non-streaming query API
          dispatch(setTypingStatus(`${botName} is typing...`));
          ConversationService.postMessages(requestData, botHeaders, widgetPreviewType)
            .then(async (response) => {
              handleNewMessages(response?.data?.payload);
              if (response?.data?.payload?.length === 0) {
                dispatch(setIsNextNodeAvailable(false));
              }
            })
            .catch((error) => {
              console.error("message not sent after exact_match:", error);
              dispatch(updateMessage({ ...data, isError: true }));
              dispatch(setTypingStatus(``));
            });
          
          return; // Exit the function early
        }

        if (chunkDone) {
          sawDone = true;
        }

        // If we got new text, accumulate and render immediately
        if (newText) {
          accumulatedText += newText;
          // Render immediately - no throttling for true real-time feel
          renderUpdate(accumulatedText);
          console.log("[Stream] Rendered:", accumulatedText.length, "chars");
        }

        if (sawDone) {
          console.log("[Stream] Done signal received");
          break;
        }
      }

      // Final cleanup
      if (accumulatedText) {
        // Ensure final state is rendered
        renderUpdate(accumulatedText);
        dispatch(setTypingStatus(``));
      } else {
        // No content received - remove placeholder
        dispatch(removeMessage({ message_id: placeholderMessageId }));
        dispatch(setTypingStatus(``));
      }

    } catch (error) {
      console.error("Smart AI streaming error:", error);
      dispatch(updateMessage({ ...data, isError: true }));
      dispatch(setTypingStatus(``));
    }
  };


  //================= GET BOT MESSAGES =================
  const getNewMessages = (request: any) => {
    console.log("[getNewMessages] Called with:", request);
    return new Promise((resolve, reject) => {
      dispatch(setTypingStatus(`${botName} is typing...`));
      ConversationService.getMessages(request, botHeaders, widgetPreviewType)
        .then(async (response) => {
          console.log("[getNewMessages] Response received");
          handleNewMessages(response?.data);
          resolve(response);
        })
        .catch((error) => {
          console.error("get messages api:", error);
          reject(error);
          dispatch(setTypingStatus(``));
        })
        .finally(() => {
          setLoading(false);
        });
    });
  };

  //================= GET BOT HISTORY MESSAGES =================
  const getHistoryMessages = async (request: any) => {
    console.log("[getHistoryMessages] Called with:", request);
    return new Promise(async (resolve, reject) => {
      setLoading(true);
      ConversationService.getHistoryMessages(request, botHeaders)
        .then(async (response) => {
          console.log("[getHistoryMessages] Response received:", response?.data?.data);
          handleHistoryMessages(response?.data?.data);
          resolve(response);
        })
        .catch((error) => {
          console.error("history get messages api:", error);
          reject(error);
        })
        .finally(() => {
          setLoading(false);
        });
    });
  };

  //================= POST BOT USER QUERY =================
  const postBotUserQuery = (data: any) => {
    // Ensure session_id is always included in the request
    const requestData = {
      ...data,
      session_id: data.session_id || sessionIdRef.current,
    };
    
    dispatch(setTypingStatus(`${botName} is typing...`));
    ConversationService.postMessages(requestData, botHeaders, widgetPreviewType)
      .then(async (response) => {
        const payload = response?.data?.payload;
        if (Array.isArray(payload) && payload.length === 0) {
          dispatch(setIsNextNodeAvailable(false));
          dispatch(setTypingStatus(``));
        } else {
          dispatch(setIsNextNodeAvailable(true));
          handleNewMessages(payload);
        }
      })
      .catch((error) => {
        console.error("message not sent:", error);
        // dispatch(removeMessage(data));
        dispatch(updateMessage({ ...data, isError: true }));
        dispatch(setTypingStatus(``));
      }).finally(() => {});  
  };

  //================= PLAY SOUND =================
  const playSoundLastBotMsg = useCallback(() => {
    if (botConfig.config?.message_tone?.enabled) {
      audio.volume = (botConfig.config?.message_tone?.volume_level || 70) / 100;
      // Handle autoplay policy - catch the error and ignore it
      audio.play().catch((error: any) => {
        console.warn('Audio autoplay prevented by browser policy:', error);
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    postBotUserQuery,
    getHistoryMessages,
    getNewMessages,
    getNewMessagesForDirectLivechat,
    getNewMessagesForSmartAI,
    loading,
    requestParams,
  };
};

export default useMessagesData;
